import * as React from 'react'

export type NavNode = {
  id: string
  label: string
  path?: string
  children?: NavNode[]
}

interface SidebarState {
  expandedRootId: string | null
  setExpandedRootId: (id: string | null) => void
}

const SidebarContext = React.createContext<SidebarState | null>(null)

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [expandedRootId, setExpandedRootId] = React.useState<string | null>(null)
  const value = React.useMemo(
    () => ({ expandedRootId, setExpandedRootId }),
    [expandedRootId],
  )

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

export function useSidebarState(): SidebarState {
  const ctx = React.useContext(SidebarContext)
  if (!ctx) {
    throw new Error('SidebarContext missing')
  }
  return ctx
}

export function flatRoots<T extends NavNode>(items: T[]): T[] {
  return items.filter((item) => item.children && item.children.length > 0)
}

export function findParentRootId<T extends NavNode>(items: T[], pathname: string): string | null {
  for (const root of flatRoots(items)) {
    if (!root.children || root.children.length === 0) {
      continue
    }

    if (root.path && root.path === pathname) {
      return root.id
    }

    const stack: NavNode[] = [...root.children]
    while (stack.length > 0) {
      const node = stack.pop()!
      if (node.path === pathname) {
        return root.id
      }
      if (node.children && node.children.length > 0) {
        stack.push(...node.children)
      }
    }
  }

  return null
}

export function getFirstChildPath<T extends NavNode>(root: T): string | null {
  const firstNavigable = getFirstNavigableNode(root)
  return firstNavigable?.path ?? null
}

export function getFirstNavigableNode<T extends NavNode>(root: T): T | null {
  const queue: T[] = [...((root.children ?? []) as T[])]
  while (queue.length > 0) {
    const node = queue.shift()!
    if (node.path) {
      return node
    }
    if (node.children && node.children.length > 0) {
      queue.push(...(node.children as T[]))
    }
  }
  return null
}
